'use client';

/**
 * Shared chrome for every customer-facing page that is *not* the menu itself ,
 * pay, reserve, booking status, loyalty, gift cards, quotes, order-ready.
 *
 * Why this exists: each of those pages had independently grown a
 * `mx-auto max-w-md space-y-4 p-6` wrapper. That is a phone layout, and on a
 * laptop it renders as a 448px ribbon of stacked cards floating in empty space.
 * It also meant every page reinvented its own loading skeleton, its own error
 * text (usually a bare red sentence with no way forward), and its own header.
 *
 * So the rules are encoded once, here:
 *   · width comes from a named intent (`narrow` / `default` / `wide` / `split`),
 *     never a raw max-w-* at the call site;
 *   · `split` gives a real desktop layout , content column + sticky summary rail
 *     , that collapses to a single column below `lg`;
 *   · loading is a shaped skeleton, not a spinner on a blank page;
 *   · an error always ships a recovery path (retry, and a way back).
 *
 * Theming: `themeId` runs through buildMenuThemePageStyle so the page adopts the
 * branch's palette and fonts , the same helper /b/[slug] and the menu use, so a
 * guest never crosses a visual seam mid-flow.
 */

import { type CSSProperties, type ReactNode } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { AlertCircle, ArrowLeft, UserRound, X, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/components/ui/utils';
import { buildMenuThemePageStyle } from '@/lib/menu-themes/page-theme';
import { branchPageThemeStyle, type BranchPageTheme } from '@/lib/menu-themes/branch-page-theme';
import { placeAccountPath } from '@/lib/routing/place-href';

export type CustomerSurfaceWidth = 'narrow' | 'default' | 'wide' | 'split';

const WIDTH_CLASS: Record<CustomerSurfaceWidth, string> = {
  // A single form or a receipt: comfortable measure, still centred on desktop.
  narrow: 'max-w-xl',
  // Default reading column , content stays legible without becoming a ribbon.
  default: 'max-w-3xl',
  // Lists and grids that genuinely benefit from the extra room.
  wide: 'max-w-6xl',
  // Content + sticky rail. Width is set on the grid itself, see CustomerSurfaceSplit.
  split: 'max-w-6xl',
};

// ── Theme plumbing ──────────────────────────────────────────────────────────

export type CustomerSurfaceBrand = {
  businessName?: string | null;
  branchName?: string | null;
  logoUrl?: string | null;
  themeId?: string | null;
  /** Palette resolved from the live branch menu; takes precedence over themeId. */
  pageTheme?: BranchPageTheme | null;
};

/** Small shared account destination for completed booking/payment task headers. */
export function CustomerAccountShortcut({
  businessSlug,
  branchSlug,
  experience = 'v1',
}: {
  businessSlug: string | null | undefined;
  branchSlug?: string | null;
  experience?: 'v1' | 'v2';
}) {
  if (!businessSlug) return null;
  return (
    <Link
      href={placeAccountPath({ business: businessSlug, branchSlug, experience })}
      aria-label="Your account"
      title="Your account"
      className="inline-flex size-10 items-center justify-center rounded-full text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-surface-hover)] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]"
    >
      <UserRound aria-hidden="true" className="size-5" />
    </Link>
  );
}

export function useCustomerSurfaceTheme(brand: CustomerSurfaceBrand | null | undefined) {
  const themed = buildMenuThemePageStyle(brand?.themeId);
  const title =
    brand?.businessName && brand?.branchName
      ? `${brand.businessName} · ${brand.branchName}`
      : brand?.businessName || brand?.branchName || null;

  // Take the merchant's COLOURS, drop their body font.
  //
  // buildMenuThemePageStyle sets `fontFamily` inline, and an inline style beats
  // any class — so on a themed page the merchant's body face silently took over
  // the entire surface, buttons included, while an unthemed page used the
  // platform control face. Two pages in the same flow rendered their "Continue"
  // button in different typefaces depending on whether the branch had picked a
  // theme.
  //
  // The rule this restores: if the guest can tap it to change state, it is the
  // platform speaking. The merchant's display face still lands on headings via
  // `headingStyle` below, which is where their identity belongs on a payment or
  // booking screen.
  const { fontFamily: _merchantBodyFont, ...styleWithoutBodyFont } = themed.style ?? {};
  const style = themed.style ? (styleWithoutBodyFont as CSSProperties) : undefined;

  return {
    style,
    headingStyle: themed.headingFont ? ({ fontFamily: themed.headingFont } as CSSProperties) : undefined,
    accent: themed.accent,
    title,
  };
}

// ── Root ────────────────────────────────────────────────────────────────────

export function CustomerSurface({
  brand,
  children,
  className,
  chow = false,
}: {
  brand?: CustomerSurfaceBrand | null;
  children: ReactNode;
  className?: string;
  /**
   * Chow guests get the Chow palette, not the branch's menu theme. The theme is
   * applied as an inline `style` here, which beat every `.chow-*` token rule on
   * the payment page — the result was a forest-green Chow frame around a blue
   * merchant card. Skipping the inline palette lets `.chow-payment` map the
   * `--color-*` roles onto Chow's tokens instead.
   */
  chow?: boolean;
}) {
  const { style: menuThemeStyle } = useCustomerSurfaceTheme(brand);
  const style = brand?.pageTheme ? branchPageThemeStyle(brand.pageTheme) : (chow ? undefined : menuThemeStyle);
  return (
    <div
      data-customer-surface=""
      style={style}
      className={cn(
        // Always own the full viewport height AND paint it. Previously this was
        // skipped whenever a theme was present, on the theory that
        // buildMenuThemePageStyle painted its own canvas — but that style is
        // applied to THIS div, so any page shorter than the viewport still
        // showed the platform's background below it. That is the white block
        // under dark content that made /b and /reserve look like two pages
        // stitched together.
        //
        // `isolate` keeps any negatively-stacked child inside this element's
        // stacking context instead of sliding behind <body>.
        'isolate min-h-dvh',
        brand?.pageTheme && 'branch-theme-scope',
        style ? '' : 'bg-[var(--color-bg-base)]',
        'text-[var(--color-text-primary)]',
        // The platform control face, matching the Place shell on /m. The
        // ownership rule is: if the guest can tap it to change state, it is the
        // platform speaking. These pages carry no merchant template body, so the
        // whole surface is platform voice — and without this, a guest moving
        // from the menu to a payment page saw every button change typeface
        // mid-flow. Merchant display faces still win where they are applied
        // explicitly, via `headingStyle`.
        'font-[family-name:var(--font-place-control)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

// ── Header ──────────────────────────────────────────────────────────────────

/**
 * Sticky, translucent, and always carries the branch identity so a guest who
 * landed here from a QR code knows whose payment page they are on. The back
 * affordance falls back to a supplied href when there is no history to pop
 * (a QR scan opens a fresh tab , `router.back()` alone is a dead button there).
 */
export function CustomerSurfaceHeader({
  brand,
  backHref,
  onBack,
  eyebrow,
  title,
  trailing,
  sticky = true,
  className,
  chow = false,
}: {
  brand?: CustomerSurfaceBrand | null;
  backHref?: string;
  onBack?: () => void;
  eyebrow?: ReactNode;
  title?: ReactNode;
  trailing?: ReactNode;
  sticky?: boolean;
  /** Extra classes on the <header>. Used by the receipt to hide chrome in print. */
  className?: string;
  /**
   * The Chow task-page bar (`app/pay/[id].tsx` `ModalBar`): close, title, and one
   * trailing fact. The V1 bar carries a merchant logo and an eyebrow, which is
   * the merchant's voice on a screen the guest reached from the Chow menu.
   */
  chow?: boolean;
}) {
  const router = useRouter();
  const { headingStyle, title: brandTitle } = useCustomerSurfaceTheme(brand);
  const showBack = Boolean(backHref || onBack);

  const goBack = () => {
    if (onBack) return onBack();
    if (backHref) return router.push(backHref);
  };

  if (chow) {
    return (
      <header
        data-customer-header=""
        className={cn('chow-payment-header', sticky && 'sticky top-0 z-30', className)}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-2 px-2 sm:px-4">
          {showBack ? (
            <button
              type="button"
              onClick={goBack}
              aria-label="Close"
              className="chow-payment-icon"
            >
              <X aria-hidden="true" className="size-5" />
            </button>
          ) : (
            <span aria-hidden="true" className="size-10 shrink-0" />
          )}
          <p data-customer-title="" className="chow-payment-title min-w-0 flex-1 truncate">
            {title || 'Pay with'}
          </p>
          {trailing ? (
            <div className="flex shrink-0 items-center gap-1">
              {trailing}
            </div>
          ) : null}
        </div>
      </header>
    );
  }

  return (
    <header
      data-customer-header=""
      className={cn(
        'border-b border-[var(--color-border-subtle)]',
        'bg-[var(--color-bg-surface)]/90 backdrop-blur-xl',
        'font-[family-name:var(--font-place-control)]',
        sticky && 'sticky top-0 z-30',
        className,
      )}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/*
        56px, matching components/place/PlaceHeader.tsx exactly. This header was
        64px, the menu's was 56px, /reserve's was 65px and /order-ready's was
        102px — four different heights for the same bar, which is most of why a
        guest crossing from the menu to a booking to a payment felt the ground
        move. The width cap is gone too: the header spans the surface and only
        its CONTENT is centred, so a wide page and a narrow page still share one
        continuous bar.
      */}
      <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-1 px-2 sm:px-4">
        {showBack ? (
          <button
            type="button"
            onClick={goBack}
            aria-label="Go back"
            className={cn(
              'inline-flex size-10 shrink-0 items-center justify-center rounded-full',
              'text-[var(--color-text-primary)] transition-colors',
              'hover:bg-[var(--color-bg-surface-hover)]',
              'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]',
            )}
          >
            <ArrowLeft aria-hidden="true" className="size-5" />
          </button>
        ) : (
          <span aria-hidden="true" className="size-10 shrink-0" />
        )}

        {brand?.logoUrl ? (
          <Image
            src={brand.logoUrl}
            alt=""
            width={32}
            height={32}
            unoptimized
            className="size-8 shrink-0 rounded-lg border border-[var(--color-border-subtle)] object-cover"
          />
        ) : null}

        <div className="min-w-0 flex-1 px-1">
          {eyebrow ? (
            <p className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-text-secondary)]">
              {eyebrow}
            </p>
          ) : null}
          <p data-customer-title="" className="truncate text-sm font-semibold leading-tight" style={headingStyle}>
            {title || brandTitle || 'Menuraq'}
          </p>
        </div>

        {trailing ? <div className="shrink-0">{trailing}</div> : null}
      </div>
    </header>
  );
}

// ── Body ────────────────────────────────────────────────────────────────────

export function CustomerSurfaceBody({
  width = 'default',
  children,
  className,
}: {
  width?: CustomerSurfaceWidth;
  children: ReactNode;
  className?: string;
}) {
  return (
    <main
      data-customer-body=""
      className={cn(
        'mx-auto w-full px-4 py-6 sm:px-6 sm:py-8 lg:py-10',
        WIDTH_CLASS[width],
        className,
      )}
    >
      {children}
    </main>
  );
}

/**
 * The desktop layout that these pages were missing. Below `lg` it is one column
 * in source order; at `lg` the aside becomes a sticky rail beside the content,
 * so an order total or a booking summary stays on screen while the guest works
 * down a form instead of scrolling away above it.
 */
export function CustomerSurfaceSplit({
  children,
  aside,
  asideFirstOnMobile = false,
  className,
}: {
  children: ReactNode;
  aside: ReactNode;
  /** Put the summary above the form on phones , right for checkout, where the
   *  amount due is the first thing a guest wants confirmed. */
  asideFirstOnMobile?: boolean;
  className?: string;
}) {
  return (
    // <main>, not <div>. CustomerSurfaceBody renders a landmark and this did
    // not, so pay/quote/loyalty — the split-layout pages — shipped with no main
    // landmark at all and a screen-reader user had no way to skip the chrome.
    <main
      data-customer-body=""
      data-customer-split=""
      className={cn(
        'mx-auto grid w-full max-w-6xl gap-6 px-4 py-6 sm:px-6 sm:py-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-8 lg:py-10',
        className,
      )}
    >
      <div className={cn('min-w-0 space-y-4', asideFirstOnMobile && 'order-2 lg:order-1')}>
        {children}
      </div>
      <aside className={cn('min-w-0', asideFirstOnMobile && 'order-1 lg:order-2')}>
        <div className="lg:sticky lg:top-24 lg:space-y-4">{aside}</div>
      </aside>
    </main>
  );
}

// ── Section ─────────────────────────────────────────────────────────────────

export function CustomerSection({
  title,
  description,
  action,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-3', className)}>
      {title || description || action ? (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {title ? <h2 className="text-base font-semibold leading-tight">{title}</h2> : null}
            {description ? (
              <p className="mt-1 text-sm leading-relaxed text-[var(--color-text-secondary)]">{description}</p>
            ) : null}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

// ── States ──────────────────────────────────────────────────────────────────

/**
 * Shaped loading. A guest who taps "pay" and gets a grey rectangle cannot tell
 * a slow network from a broken link, so the skeleton mirrors the layout that is
 * about to appear , header bar, hero figure, then rows , and it is announced to
 * assistive tech rather than being silent visual noise.
 */
export function CustomerSurfaceLoading({
  width = 'default',
  label = 'Loading…',
  rows = 3,
  withHero = true,
}: {
  width?: CustomerSurfaceWidth;
  label?: string;
  rows?: number;
  withHero?: boolean;
}) {
  return (
    <div className="min-h-screen bg-[var(--color-bg-base)]" role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">{label}</span>
      <div className="border-b border-[var(--cs-border)]/70">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-3 px-4 sm:px-6">
          <Skeleton className="size-10 rounded-xl" />
          <Skeleton className="h-4 w-40 rounded-full" />
        </div>
      </div>
      <div className={cn('mx-auto w-full space-y-4 px-4 py-6 sm:px-6 sm:py-8', WIDTH_CLASS[width])}>
        {withHero ? <Skeleton className="h-32 w-full rounded-[var(--radius-lg)]" /> : null}
        {Array.from({ length: rows }).map((_, index) => (
          <Skeleton
            key={index}
            className="w-full rounded-[var(--radius-lg)]"
            style={{ height: `${96 + index * 24}px` }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Never a bare red sentence. An error state names what failed in plain language,
 * and offers at least one way forward , retrying in place is almost always
 * possible here, since every one of these pages is a single GET away from working.
 */
export function CustomerSurfaceError({
  title = 'We couldn’t load this page',
  message,
  onRetry,
  retryLabel = 'Try again',
  secondaryAction,
  icon: Icon = AlertCircle,
  brand,
}: {
  title?: string;
  message?: string | null;
  onRetry?: () => void;
  retryLabel?: string;
  secondaryAction?: ReactNode;
  icon?: LucideIcon;
  brand?: CustomerSurfaceBrand | null;
}) {
  return (
    <CustomerSurface brand={brand}>
      <CustomerSurfaceBody width="narrow">
        <div
          role="alert"
          className="flex flex-col items-center rounded-[var(--radius-lg)] border border-[var(--cs-border)] bg-card px-6 py-14 text-center"
        >
          <span className="flex size-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <Icon className="size-7" aria-hidden="true" />
          </span>
          <h1 className="mt-5 text-lg font-semibold">{title}</h1>
          {message ? (
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-[var(--color-text-secondary)]">{message}</p>
          ) : null}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {onRetry ? (
              <Button onClick={onRetry} className="min-h-11">
                {retryLabel}
              </Button>
            ) : null}
            {secondaryAction}
          </div>
        </div>
      </CustomerSurfaceBody>
    </CustomerSurface>
  );
}

/**
 * Inline, polite status line for async work that happens *inside* a page that is
 * already rendered , applying a promo code, waiting on a MoMo prompt. Screen
 * readers get it through aria-live; sighted users get a moving dot so a slow
 * network still looks alive.
 */
export function CustomerSurfaceStatus({
  tone = 'info',
  children,
  className,
}: {
  tone?: 'info' | 'success' | 'error' | 'pending';
  children: ReactNode;
  className?: string;
}) {
  const toneClass = {
    info: 'border-[var(--cs-border)] bg-[var(--color-bg-sunken)]/50 text-[var(--color-text-primary)]',
    success: 'border-[var(--color-success)]/30 bg-[var(--color-success-soft)] text-[var(--color-success)]',
    error: 'border-destructive/30 bg-destructive/10 text-destructive',
    pending: 'border-[var(--cs-border)] bg-[var(--color-bg-sunken)]/50 text-[var(--color-text-primary)]',
  }[tone];

  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      className={cn(
        'flex items-center gap-2 rounded-[var(--radius-md)] border px-3 py-2.5 text-sm leading-relaxed',
        toneClass,
        className,
      )}
    >
      {tone === 'pending' ? (
        <span className="flex shrink-0 gap-1" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="size-1.5 animate-bounce rounded-full bg-current"
              style={{ animationDelay: `${i * 140}ms` }}
            />
          ))}
        </span>
      ) : null}
      <span className="min-w-0">{children}</span>
    </p>
  );
}
